import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isValid,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

const WEEKDAYS_JA = ['日', '月', '火', '水', '木', '金', '土'];
const WEEKDAYS_ZH = ['日', '一', '二', '三', '四', '五', '六'];
const WEEKDAYS_EN = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

interface DatePickerProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'value' | 'onChange'
> {
  /** ISO date (`yyyy-MM-dd`) or empty string. */
  value?: string;
  /** Receives `yyyy-MM-dd`, or empty string when cleared. */
  onChange: (value: string) => void;
  placeholder?: string;
  minDate?: string;
  maxDate?: string;
}

/**
 * Calendar-popover date field. The masters forms previously relied on the
 * native `<input type="date">`, which on Safari renders as a bare spinner
 * field with no calendar at all (customer report 2026-07-30) — this gives
 * every browser the same explicit picker.
 *
 * Extra button attributes (`id`, `aria-describedby`, `aria-invalid`, …) are
 * spread onto the trigger, so react-hook-form's `FormControl` accessibility
 * contract keeps working.
 */
export const DatePicker = ({
  value = '',
  onChange,
  disabled,
  placeholder,
  minDate,
  maxDate,
  className,
  ...buttonProps
}: DatePickerProps) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const selected = React.useMemo(() => {
    if (!value) return null;
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : null;
  }, [value]);
  const min = React.useMemo(() => {
    if (!minDate) return null;
    const parsed = parseISO(minDate);
    return isValid(parsed) ? parsed : null;
  }, [minDate]);
  const max = React.useMemo(() => {
    if (!maxDate) return null;
    const parsed = parseISO(maxDate);
    return isValid(parsed) ? parsed : null;
  }, [maxDate]);

  const [month, setMonth] = React.useState<Date>(() => selected ?? min ?? new Date());
  React.useEffect(() => {
    if (open) setMonth(selected ?? min ?? new Date());
  }, [min, open, selected]);

  const lang = i18n.language;
  const isCjk = lang.startsWith('zh') || lang.startsWith('ja');
  const weekdays = lang.startsWith('zh')
    ? WEEKDAYS_ZH
    : lang.startsWith('ja')
      ? WEEKDAYS_JA
      : WEEKDAYS_EN;
  const monthLabel = isCjk ? format(month, 'yyyy年M月') : format(month, 'MMM yyyy');
  const days = eachDayOfInterval({
    start: startOfWeek(startOfMonth(month)),
    end: endOfWeek(endOfMonth(month)),
  });
  const previousMonth = addMonths(month, -1);
  const nextMonth = addMonths(month, 1);
  const previousMonthDisabled = !!min && isBefore(endOfMonth(previousMonth), min);
  const nextMonthDisabled = !!max && isAfter(startOfMonth(nextMonth), max);
  const today = new Date();

  return (
    // modal: keeps the calendar usable inside Radix Dialogs/Sheets — the
    // dialog focus trap would otherwise dismiss the popover on open.
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          {...buttonProps}
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal',
            !selected && 'text-muted-foreground',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selected ? format(selected, 'yyyy/MM/dd') : (placeholder ?? '—')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" align="start">
        <div className="flex items-center justify-between pb-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label={t('efs.common.date.prev_month', {
              ns: 'common',
              defaultValue: '前の月',
            })}
            disabled={previousMonthDisabled}
            onClick={() => setMonth(previousMonth)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium">{monthLabel}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label={t('efs.common.date.next_month', {
              ns: 'common',
              defaultValue: '次の月',
            })}
            disabled={nextMonthDisabled}
            onClick={() => setMonth(nextMonth)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid grid-cols-7 gap-y-1 text-center">
          {weekdays.map((d) => (
            <span key={d} className="text-xs text-muted-foreground">
              {d}
            </span>
          ))}
          {days.map((day) => {
            const isSelected = !!selected && isSameDay(day, selected);
            const isOutOfRange = (!!min && isBefore(day, min)) || (!!max && isAfter(day, max));
            return (
              <button
                key={day.toISOString()}
                type="button"
                aria-label={format(day, 'yyyy-MM-dd')}
                disabled={isOutOfRange}
                onClick={() => {
                  if (isOutOfRange) return;
                  onChange(format(day, 'yyyy-MM-dd'));
                  setOpen(false);
                }}
                className={cn(
                  'mx-auto flex h-8 w-8 items-center justify-center rounded-md text-sm hover:bg-accent',
                  !isSameMonth(day, month) && 'text-muted-foreground/50',
                  isOutOfRange && 'cursor-not-allowed opacity-30 hover:bg-transparent',
                  isSameDay(day, today) && !isSelected && 'border border-input',
                  isSelected && 'bg-primary text-primary-foreground hover:bg-primary',
                )}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>
        {selected ? (
          <div className="pt-2 text-right">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              {t('efs.common.actions.clear')}
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
};
